'use client';

import Image from 'next/image';
import Link from 'next/link';
import { DEFAULT_OPTION } from '@/lib/constants/content';
import { useCart } from './cart-context';
import { DeleteItemButton } from '@/components/commerce/cart/delete-item-button';
import { EditItemQuantityButton } from '@/components/commerce/cart/edit-item-quantity-button';
import Price from '@/components/commerce/price';
import { createUrl } from '@/lib/utils';
import { useModal } from '@/components/modal/ModalProvider';
import { useCampaign } from '@/app/CampaignContext';
import {
  calculateItemCost,
  getLineItemCostAndSavings,
  totalsDisplayPrice
} from './cart-price-utils';
import {
  lineItemClassName,
  lineItemThumbnailClassName,
  lineItemThumbnailFrameClassName
} from './cart-summary-parts';

/**
 * One line in the cart used in `CartSummary`.
 *
 * `CartSummarySkeleton`'s `LineItemSkeleton` is this component's stand-in,
 * so take a look at that when updating this.
 */
export default function CartLineItem({ item, position }) {
  const { updateCartItem } = useCart();
  const { showPriceDetails } = useModal();
  const campaign = useCampaign();

  const merchandiseUrl = getMerchandiseUrl(item);

  const GWPItem =
    item.attributes?.find((attribute) => attribute.key.includes('promoType'))
      ?.value === 'free';

  function handlePriceModalClick(e, variant) {
    e.preventDefault();
    showPriceDetails(
      `cart-summary-${variant}`,
      null,
      campaign?.pricing_details || ''
    );
  }

  return (
    <li
      className={lineItemClassName}
      data-cy="cart-item"
      data-handle={item.merchandise.product.handle}
      data-variant-title={item.merchandise.title}
    >
      <div className={lineItemThumbnailFrameClassName}>
        {GWPItem ? (
          <LineItemThumbnail item={item} />
        ) : (
          <Link
            href={merchandiseUrl}
            prefetch={false}
            className="flex flex-col"
          >
            <LineItemThumbnail item={item} />
          </Link>
        )}
      </div>

      <div className="flex w-full flex-col justify-between">
        <div className="flex flex-row">
          <div className="w-full flex-row lg:flex">
            <div className="relative flex flex-1 flex-col">
              <div className="ml-4 pb-2">
                <div className="body-s flex w-full flex-row justify-between">
                  {GWPItem ? (
                    <span className="body-s">
                      {item.merchandise.product.title}
                    </span>
                  ) : (
                    <Link
                      href={merchandiseUrl}
                      className="flex flex-col"
                      prefetch={false}
                    >
                      <span className="body-s">
                        {item.merchandise.product.title}
                      </span>
                    </Link>
                  )}
                  {!GWPItem && (
                    <div className="relative flex flex-col">
                      <DeleteItemButton
                        item={item}
                        optimisticUpdate={updateCartItem}
                        position={position}
                      />
                    </div>
                  )}
                </div>
                <span className="body-xs text-grey-text block">
                  {item.merchandise.title}
                </span>
                <BundleAttributeLabels item={item} />
              </div>
            </div>
          </div>
        </div>
        {/* Start of price details */}
        <div className="ml-4 flex justify-between">
          <div className="flex self-end">
            <div className="align-items grid grid-cols-3 items-center">
              <EditItemQuantityButton
                item={item}
                type="minus"
                position={position}
              />
              <span className="sr-only">
                current quantity of {item.title} {item.merchandise.title} in
                cart is
              </span>
              <span
                data-value={item.quantity}
                className="font-lato col-start-2 pr-2 pl-1 text-center"
                data-cy="quantity"
              >
                {item.quantity}
              </span>
              <EditItemQuantityButton
                item={item}
                type="plus"
                position={position}
              />
            </div>
          </div>
          <div className="justify-self-end">
            <div>
              <div className="lg:flex">
                <div className="relative">
                  <button
                    className="body-xs text-grey-text expanded-hitbox mr-2 hidden cursor-pointer self-center pt-px text-right underline lg:block"
                    onClick={(e) =>
                      handlePriceModalClick(e, item.merchandise.product.handle)
                    }
                  >
                    Price details
                  </button>
                </div>
                <div className="flex justify-end text-right text-sm">
                  {/* Crossed out Compare At Price */}
                  <del className="price-s-crossed mr-1">
                    <Price
                      amount={calculateItemCost(
                        item.quantity,
                        item.merchandise.compareAtPrice.amount
                      )}
                      currencyCode={
                        item.merchandise.compareAtPrice.currencyCode
                      }
                    />
                  </del>
                  {/* Sales Price */}
                  <Price
                    className="price-s"
                    amount={getLineItemCostAndSavings(item).subtotal}
                    currencyCode={item.merchandise.price.currencyCode}
                  />
                </div>
                <div className="relative flex justify-end text-right">
                  <button
                    className="body-xs text-grey-text expanded-hitbox cursor-pointer text-right underline lg:hidden"
                    onClick={(e) =>
                      handlePriceModalClick(e, item.merchandise.product.handle)
                    }
                  >
                    Price details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

const LineItemThumbnail = ({ item }) => {
  return (
    <div className="relative overflow-hidden">
      <Image
        className={`object-cover ${lineItemThumbnailClassName}`}
        width="80"
        height="80"
        sizes="(min-width: 1024px) 80px, 52px"
        alt={item?.merchandise?.product.title}
        src={
          item?.merchandise?.image?.url ??
          item?.merchandise?.product?.featuredImage?.url
        }
      />
    </div>
  );
};

const BundleAttributeLabels = ({ item }) => {
  return item?.attributes?.map((attribute, i) => {
    if (attribute.key === 'Bundle') {
      // Only show the bundle name, do not append the product name
      return (
        <p key={i} className="body-xs text-grey-text">
          Bundle: {attribute.value}
        </p>
      );
    }
    if (attribute.key.includes('bundleDiscountAmount')) {
      let discountAmount = parseFloat(attribute.value * item.quantity);
      const compareAtPrice =
        parseFloat(item.merchandise.compareAtPrice.amount) * item.quantity;

      // Validate that discount doesn't exceed compare at price
      if (discountAmount < 0) {
        discountAmount = 0;
      } else if (discountAmount > compareAtPrice) {
        discountAmount = compareAtPrice;
      }

      if (discountAmount > 0) {
        return (
          <p key={i} className="body-xs text-grey-text">
            BUNDLE SAVINGS -$
            {totalsDisplayPrice(discountAmount)}
          </p>
        );
      }
    }
    return null;
  });
};

/**
 * Takes in a line item and returns the URL for the merchandise, based on
 * whether the item is a bundle or not.
 */
function getMerchandiseUrl(item) {
  // Find the bundle handle, if any.
  const bundleHandle = item.attributes?.find((attribute) =>
    attribute.key.includes('_bundleHandle')
  )?.value;
  // Create search params from the selected options.
  const merchandiseSearchParams = {};
  item.merchandise.selectedOptions.forEach(({ name, value }) => {
    if (value !== DEFAULT_OPTION) {
      merchandiseSearchParams[name.toLowerCase()] = value;
    }
  });

  let merchandiseUrl;
  if (bundleHandle) {
    merchandiseUrl = bundleHandle;
  } else {
    // Not a bundle, so use the product handle.
    merchandiseUrl = createUrl(
      `/products/${item.merchandise.product.handle}`,
      new URLSearchParams(merchandiseSearchParams)
    );
  }

  return merchandiseUrl;
}
